import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  model,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { DialogService, DynamicDialogRef } from '../../core/services/dialog';
import { NotificationsListComponent } from './notifications-list/notifications-list.component';
import { Subscription } from 'rxjs';
import { NotificationService } from '../../core/services/notification/notification.service';
import {
  ActivityItemComponent,
  NotificationType,
} from '../../shared/components/activity-item-notification/activity-item.component';
import { TextButton } from '../../shared/components/buttons/text-button/text-button';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';
import { IcNotificationComponent } from '../../shared/components/Icons/ic-notification/ic-notification';
import { ActivityItemNotification } from '../../shared/models/activity-item.model';
import {
  PaginatedQueryOptions,
  PaginatedQueryResult,
} from '../../shared/models/common.model';
import { TranslatePipe } from '../../shared/pipes/translate/translate.pipe';

/**
 * Interface representing a notification object from the server.
 */
export interface UserNotification {
  id: number;
  userId: number;
  clientId?: number | null;
  brokerId?: number | null;
  title: string;
  message: string;
  moduleId?: string;
  moduleName?: string;
  type: 'warning' | 'info' | 'error' | 'success';
  isRead: boolean;
  readAt: string | null;
  isDeleted: boolean;
  metadata: {
    declarations?: string[];
    [key: string]: unknown;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Maps notification types to their corresponding themed colors.
 */
const NOTIFICATION_ICON_COLOR: Record<NotificationType, string> = {
  info: 'var(--color-primary)',
  success: 'var(--color-st-tick)',
  warning: 'var(--color-accent)',
  error: 'var(--color-danger)',
};

/**
 * Component responsible for displaying and managing user notifications.
 * It supports pagination, marking as read, and real-time updates via external triggers.
 */
@Component({
  selector: 'app-notifications',
  imports: [
    CommonModule,
    ActivityItemComponent,
    TranslatePipe,
    EmptyStateComponent,
    TextButton,
  ],
  standalone: true,
  templateUrl: './notifications-component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./notifications-component.scss'],
})
export class NotificationsComponent implements OnInit, OnDestroy {
  /**
   * Callback to refresh unread count in parent components.
   */
  @Input() refreshUnreadCallback!: () => void;

  /**
   * Indicates whether the component is embedded inside a combined tab interface.
   */
  @Input() isEmbedded = false;

  /**
   * Visibility state model for the notification drawer/dialog.
   */
  showNotifications = model(false);

  /**
   * Emits when the total unread count changes.
   */
  @Output() unreadCountChange = new EventEmitter<number>();

  // Services
  private dialogRef = inject(DynamicDialogRef);
  private dialogService = inject(DialogService);
  public translate = inject(TranslatePipe);
  private router = inject(Router);
  private notificationService = inject(NotificationService);

  // State Signals
  public notifications = signal<UserNotification[]>([]);
  public isLoading = signal<boolean>(false);
  public hasMore = signal<boolean>(true);
  public page = signal<number>(1);
  public totalUnreadCount = signal<number>(0);

  /**
   * Computed property that transforms raw notifications into ActivityItemNotifications
   * for the display component.
   */
  activityItems = computed(() => {
    const data = this.notifications();
    if (!Array.isArray(data)) return [];
    return data.map((notif) => this.mapNotificationToActivityItem(notif));
  });

  private socketSub?: Subscription;
  private reconnectSub?: Subscription;

  /**
   * Groups notifications by date sections: Today, Yesterday, and Older.
   */
  groupedNotifications = computed(() => {
    const items = this.activityItems();
    if (!items.length) return [];

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups: {
      title: string;
      unreadCount: number;
      items: ActivityItemNotification<UserNotification>[];
    }[] = [
      { title: 'today', unreadCount: 0, items: [] },
      { title: 'yesterday', unreadCount: 0, items: [] },
      { title: 'older', unreadCount: 0, items: [] },
    ];

    items.forEach((item) => {
      const itemDate = new Date(item.date);
      const itemDay = new Date(
        itemDate.getFullYear(),
        itemDate.getMonth(),
        itemDate.getDate()
      );

      let groupIndex = 2; // Default to Older
      if (itemDay.getTime() === today.getTime()) {
        groupIndex = 0;
      } else if (itemDay.getTime() === yesterday.getTime()) {
        groupIndex = 1;
      }

      groups[groupIndex].items.push(item);
      if (!item.isRead) {
        groups[groupIndex].unreadCount++;
      }
    });

    return groups.filter((g) => g.items.length > 0);
  });

  ngOnInit(): void {
    this.loadNotifications();
  }

  /**
   * Cleans up subscriptions to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this.socketSub?.unsubscribe();
    this.reconnectSub?.unsubscribe();
  }

  /**
   * Closes the notification dialog/drawer.
   */
  onClose() {
    this.dialogRef.close();
  }

  /**
   * Maps a notification object to the ActivityItemNotification structure.
   * @param notification The raw notification data.
   * @returns A structured ActivityItemNotification for display.
   */
  mapNotificationToActivityItem(
    notification: UserNotification
  ): ActivityItemNotification<UserNotification> {
    return {
      id: notification.id,
      content: notification.message ?? '',
      date: notification.createdAt
        ? new Date(notification.createdAt)
        : new Date(),
      icon: IcNotificationComponent,
      hasLink: !!notification.moduleId,
      isRead: notification.isRead ?? false,
      type: notification.type,
      data: notification,
      title: notification.title,
      startColor: NOTIFICATION_ICON_COLOR[notification.type],
      endColor: NOTIFICATION_ICON_COLOR[notification.type],
    };
  }

  /**
   * Handles scroll events to trigger infinite loading.
   * @param event The scroll event.
   */
  onScroll(event: Event) {
    const target = event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Trigger load more when user is near bottom (100px)
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      this.loadMore();
    }
  }

  /**
   * Increments the page and loads more notifications if available.
   */
  loadMore() {
    if (this.isLoading() || !this.hasMore()) return;
    this.page.update((p) => p + 1);
    this.loadNotifications();
  }

  /**
   * Fetches notifications from the server based on current pagination status.
   */
  public loadNotifications(): void {
    if (this.isLoading()) return;

    this.isLoading.set(true);

    const opts: PaginatedQueryOptions = {
      page: this.page(),
      itemsPerPage: 20,
    };

    this.notificationService.getNotifications(opts).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        if (res.success && res.data) {
          let newNotifications: UserNotification[] = [];

          if (Array.isArray(res.data)) {
            newNotifications = res.data as unknown as UserNotification[];
          } else if (
            res.data &&
            typeof res.data === 'object' &&
            'result' in res.data
          ) {
            const paginatedRes =
              res.data as unknown as PaginatedQueryResult<UserNotification>;
            if (Array.isArray(paginatedRes.result)) {
              newNotifications =
                paginatedRes.result as unknown as UserNotification[];
            }
          }

          if (this.page() === 1) {
            this.notifications.set(newNotifications);
          } else {
            this.notifications.update((current) => [
              ...current,
              ...newNotifications,
            ]);
          }

          if (newNotifications.length < 20) {
            this.hasMore.set(false);
          }

          this.updateUnreadCount();
        }
      },
      error: (err) => {
        this.isLoading.set(false);
        console.error('Error fetching notifications:', err);
      },
    });
  }

  /**
   * Updates the internal unread count signal and emits the change.
   */
  private updateUnreadCount(): void {
    const count = this.notifications().filter((n) => !n.isRead).length;
    this.totalUnreadCount.set(count);
    this.unreadCountChange.emit(count);
  }

  /**
   * Marks a specific notification as read.
   * @param notificationId The ID of the notification.
   */
  markAsRead(notificationId: number | string) {
    const numericId = Number(notificationId);
    if (isNaN(numericId)) return;

    // Immediate UI feedback
    this.notifications.update((current) =>
      current.map((n) =>
        n.id === numericId
          ? { ...n, isRead: true, readAt: new Date().toISOString() }
          : n
      )
    );

    this.updateUnreadCount();

    // Call service to update server
    this.notificationService.markAsRead(numericId).subscribe({
      next: (res) => {
        if (!res.success) {
          console.error('Failed to mark notification as read on server');
        }
      },
    });

    // Call callback if provided
    if (this.refreshUnreadCallback) {
      this.refreshUnreadCallback();
    }
  }

  /**
   * Handles clicking on a notification item.
   * Navigates to the relevant module if applicable.
   * @param notification The clicked notification.
   */
  onNotificationClick(notification?: UserNotification) {
    if (!notification) return;

    this.markAsRead(notification.id);

    if (
      notification.moduleName === 'CustomsShipment' &&
      notification.moduleId
    ) {
      this.router.navigate([
        '/shipments/details',
        notification.moduleId,
        'info',
      ]);
      this.onClose();
    } else if (
      (notification.moduleName === 'TransitDeclaration' ||
        notification.moduleName === 'Transit') &&
      notification.moduleId
    ) {
      this.router.navigate([
        '/transit-declarations/transit-departure/details',
        notification.moduleId,
      ]);
      this.onClose();
    }
  }

  /**
   * Marks all notifications as read.
   */
  onMarkAllAsRead() {
    this.notifications.update((current) =>
      current.map((n) => ({
        ...n,
        isRead: true,
        readAt: new Date().toISOString(),
      }))
    );

    this.totalUnreadCount.set(0);
    this.unreadCountChange.emit(0);

    // Call service to update server
    this.notificationService.markAllAsRead().subscribe({
      next: (res) => {
        if (!res.success) {
          console.error('Failed to mark all as read on server');
        }
      },
    });

    if (this.refreshUnreadCallback) {
      this.refreshUnreadCallback();
    }
  }

  /**
   * Navigates to the full notifications list page.
   */
  onViewAll() {
    this.dialogService.open(NotificationsListComponent, {
      header: this.translate.transform('notifications'),
      width: '80%',
      height: '90%',
      baseZIndex: 10001,
      modal: true,
      dismissableMask: true,
      closable: true,
      styleClass: 'custom-header',
    });
    this.onClose();
  }
}
